import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, Loading } from "@/components/ui-bits";
import { Bell, ShieldCheck, PoundSterling, CheckCircle2, Wrench, Check, ChevronRight } from "lucide-react";

const catIcon = {
  Compliance: { icon: ShieldCheck, color: "text-amber-700 bg-amber-50" },
  Finance: { icon: PoundSterling, color: "text-emerald-700 bg-emerald-50" },
  Rent: { icon: PoundSterling, color: "text-emerald-700 bg-emerald-50" },
  Approval: { icon: CheckCircle2, color: "text-blue-700 bg-blue-50" },
  Maintenance: { icon: Wrench, color: "text-violet-700 bg-violet-50" },
};

export default function Notifications() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const markRead = async (n) => {
    setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
    await api.patch(`/notifications/${n.notification_id}/read`);
  };
  const openNotification = (n) => {
    if (!n.read) markRead(n);
    if (n.link) navigate(n.link);
  };

  if (!items) return <Layout title="Notifications"><Loading /></Layout>;
  const timeAgo = (d) => {
    const h = Math.round((Date.now() - new Date(d)) / 3600000);
    if (h < 1) return "Just now";
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  return (
    <Layout title="Notification Centre" subtitle="Stay Informed">
      <div className="max-w-3xl space-y-3">
        {items.length === 0 && <p className="text-sm text-slate-400">No notifications.</p>}
        {items.map((n) => {
          const meta = catIcon[n.category] || { icon: Bell, color: "text-slate-700 bg-slate-100" };
          const Icon = meta.icon;
          return (
            <Card key={n.notification_id} data-testid={`notification-${n.notification_id}`}
              onClick={n.link ? () => openNotification(n) : undefined}
              className={`p-4 flex items-start gap-4 fade-up ${n.read ? "opacity-70" : ""} ${n.link ? "cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all" : ""}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}><Icon size={18} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                  {!n.read && <span className="w-2 h-2 bg-blue-600 rounded-full" />}
                  <Badge>{n.category}</Badge>
                  <span className="text-xs text-slate-400 ml-auto">{timeAgo(n.date)}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{n.body}</p>
                {n.link && <span className="text-xs font-medium text-blue-700 mt-1.5 inline-flex items-center gap-0.5" data-testid={`notification-open-${n.notification_id}`}>Open <ChevronRight size={12} /></span>}
              </div>
              {!n.read && (
                <button onClick={(e) => { e.stopPropagation(); markRead(n); }} data-testid={`mark-read-${n.notification_id}`} className="text-slate-400 hover:text-blue-700 transition-colors" title="Mark as read">
                  <Check size={18} />
                </button>
              )}
            </Card>
          );
        })}
      </div>
    </Layout>
  );
}
